import { CustomField, PhoneFieldRole } from '../types/customField.types';

export const DEFAULT_PHONE_COUNTRY_CODE = '212';

export interface ResolvedPhoneEntry {
  field: CustomField;
  value: string;
  configuredRole: PhoneFieldRole;
  effectiveRole: PhoneFieldRole;
}

export interface ResolvedPhoneContacts {
  entries: ResolvedPhoneEntry[];
  callPhone: string | null;
  whatsappPhone: string | null;
  primaryDisplayPhone: string | null;
  validationError: string | null;
}

export const getPhoneFieldRole = (field?: Pick<CustomField, 'phone_role'> | null): PhoneFieldRole => {
  return field?.phone_role || 'both';
};

export const normalizePhoneDisplay = (value?: string | null): string => {
  return String(value || '').trim();
};

/** Indicatifs sans zéro national (ex. NANP). */
const DIAL_WITHOUT_TRUNK_ZERO = new Set(['1']);

/**
 * Format local boutique (chiffres, souvent avec 0), sans espaces / tirets / indicatif.
 * Ex. MA : "+212 6-12-34-56-78" → "0612345678"
 */
export const normalizeStoreLocalPhone = (
  value?: string | null,
  dialCode = DEFAULT_PHONE_COUNTRY_CODE
): string => {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);

  const dial = String(dialCode || DEFAULT_PHONE_COUNTRY_CODE).replace(/\D/g, '')
    || DEFAULT_PHONE_COUNTRY_CODE;
  const useTrunkZero = !DIAL_WITHOUT_TRUNK_ZERO.has(dial);

  if (digits.startsWith(dial) && digits.length >= dial.length + 8) {
    const national = digits.slice(dial.length);
    if (!useTrunkZero) return national;
    return national.startsWith('0') ? national : `0${national}`;
  }

  if (digits.startsWith('0') && digits.length >= 9) {
    return digits;
  }

  if (!digits.startsWith('0') && digits.length >= 8 && digits.length <= 10) {
    return useTrunkZero ? `0${digits}` : digits;
  }

  return digits;
};

export const normalizePhoneDigits = (
  value?: string | null,
  defaultCountryCode = DEFAULT_PHONE_COUNTRY_CODE
): string => {
  const raw = normalizePhoneDisplay(value);
  if (!raw) return '';

  // +33... / 0033... = déjà international : ne pas forcer le 212
  const hasExplicitIntl = raw.startsWith('+') || raw.startsWith('00');
  let digits = raw.replace(/\D/g, '');
  if (digits.length < 8) return '';
  if (digits.startsWith('00')) {
    digits = digits.slice(2);
  }
  if (hasExplicitIntl) {
    return digits;
  }
  if (digits.startsWith(defaultCountryCode) && digits.length >= defaultCountryCode.length + 8) {
    return digits;
  }
  // Ex. 33614202425 sans + : déjà un E.164 probable
  if (!digits.startsWith('0') && digits.length >= 11) {
    return digits;
  }
  if (digits.startsWith('0')) {
    digits = digits.slice(1);
  }
  return defaultCountryCode + digits;
};

export const isPhoneQuery = (value?: string | null): boolean => {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  if (!/^[+\d][\d\s().+-]*$/.test(raw)) return false;
  return raw.replace(/\D/g, '').length >= 2;
};

/** 06 ↔ indicatif du magasin + suite. Sert à la recherche (stockage = format local). */
export const phoneQueryVariants = (
  value?: string | null,
  dialCode = DEFAULT_PHONE_COUNTRY_CODE
): string[] => {
  const raw = String(value ?? '').trim();
  if (!raw) return [];
  if (!isPhoneQuery(raw)) return [raw];

  const dial = String(dialCode || DEFAULT_PHONE_COUNTRY_CODE).replace(/\D/g, '') || DEFAULT_PHONE_COUNTRY_CODE;
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);

  const variants = new Set<string>();
  const add = (variant: string) => {
    const text = String(variant || '').trim();
    if (text) variants.add(text);
  };

  add(raw);
  add(digits);

  const addIntl = (national: string) => {
    if (!national) return;
    const intl = dial + national;
    if (intl.length < 4) return;
    add(intl);
    add(`+${intl}`);
    add(`00${intl}`);
  };

  if (digits.startsWith('0') && digits.length >= 2) {
    const national = digits.slice(1);
    addIntl(national);
    if (national.length >= 8) add(national);
  } else if (digits.startsWith(dial) && digits.length > dial.length) {
    const national = digits.slice(dial.length);
    const intl = dial + national;
    if (national && intl.length >= 4) {
      add(`0${national}`);
      if (national.length >= 8) add(national);
      add(intl);
      add(`+${intl}`);
      add(`00${intl}`);
    }
  } else if (digits.length >= 8 && digits.length <= 10) {
    add(`0${digits}`);
    addIntl(digits);
  }

  return Array.from(variants);
};

export const isValidPhoneValue = (value?: string | null): boolean => {
  const digits = normalizePhoneDigits(value);
  return digits.length >= 10 && digits.length <= 15;
};

export const getCallHref = (value?: string | null): string | null => {
  const digits = normalizePhoneDigits(value);
  return digits ? `tel:+${digits}` : null;
};

export const getWhatsAppHref = (value?: string | null): string | null => {
  const digits = normalizePhoneDigits(value);
  return digits ? `https://wa.me/${digits}` : null;
};

export const getPhoneRoleLabel = (role: PhoneFieldRole): string => {
  switch (role) {
    case 'call':
      return 'Appel';
    case 'whatsapp':
      return 'WhatsApp';
    default:
      return 'Appel + WhatsApp';
  }
};

export const resolvePhoneContacts = (
  phoneFields: CustomField[],
  getValue: (field: CustomField) => string | null | undefined
): ResolvedPhoneContacts => {
  const filledEntries = phoneFields
    .map((field) => ({
      field,
      value: normalizePhoneDisplay(getValue(field)),
      configuredRole: getPhoneFieldRole(field)
    }))
    .filter((entry) => entry.value !== '');

  if (filledEntries.length === 0) {
    return {
      entries: [],
      callPhone: null,
      whatsappPhone: null,
      primaryDisplayPhone: null,
      validationError: null
    };
  }

  if (filledEntries.length === 1) {
    const [entry] = filledEntries;
    return {
      entries: [{ ...entry, effectiveRole: 'both' }],
      callPhone: entry.value,
      whatsappPhone: entry.value,
      primaryDisplayPhone: entry.value,
      validationError: null
    };
  }

  if (filledEntries.length > 2) {
    return {
      entries: [],
      callPhone: null,
      whatsappPhone: null,
      primaryDisplayPhone: null,
      validationError: 'Vous ne pouvez pas renseigner plus de deux numéros de téléphone client.'
    };
  }

  const [firstEntry, secondEntry] = filledEntries;
  const firstRole = firstEntry.configuredRole;
  const secondRole = secondEntry.configuredRole;

  const resolveRoles = (): ResolvedPhoneEntry[] | null => {
    if (firstRole === 'call' && secondRole === 'whatsapp') {
      return [
        { ...firstEntry, effectiveRole: 'call' },
        { ...secondEntry, effectiveRole: 'whatsapp' }
      ];
    }

    if (firstRole === 'whatsapp' && secondRole === 'call') {
      return [
        { ...firstEntry, effectiveRole: 'whatsapp' },
        { ...secondEntry, effectiveRole: 'call' }
      ];
    }

    if (firstRole === 'both' && secondRole === 'call') {
      return [
        { ...firstEntry, effectiveRole: 'whatsapp' },
        { ...secondEntry, effectiveRole: 'call' }
      ];
    }

    if (firstRole === 'call' && secondRole === 'both') {
      return [
        { ...firstEntry, effectiveRole: 'call' },
        { ...secondEntry, effectiveRole: 'whatsapp' }
      ];
    }

    if (firstRole === 'both' && secondRole === 'whatsapp') {
      return [
        { ...firstEntry, effectiveRole: 'call' },
        { ...secondEntry, effectiveRole: 'whatsapp' }
      ];
    }

    if (firstRole === 'whatsapp' && secondRole === 'both') {
      return [
        { ...firstEntry, effectiveRole: 'whatsapp' },
        { ...secondEntry, effectiveRole: 'call' }
      ];
    }

    return null;
  };

  const resolvedEntries = resolveRoles();
  if (!resolvedEntries) {
    return {
      entries: [],
      callPhone: null,
      whatsappPhone: null,
      primaryDisplayPhone: null,
      validationError:
        'Deux numéros sont renseignés, mais leurs rôles Appel / WhatsApp ne permettent pas de les départager.'
    };
  }

  const callEntry = resolvedEntries.find((entry) => entry.effectiveRole === 'call');
  const whatsappEntry = resolvedEntries.find((entry) => entry.effectiveRole === 'whatsapp');

  return {
    entries: resolvedEntries,
    callPhone: callEntry?.value || null,
    whatsappPhone: whatsappEntry?.value || null,
    primaryDisplayPhone: callEntry?.value || whatsappEntry?.value || resolvedEntries[0]?.value || null,
    validationError: null
  };
};
